import React, { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";

export default function Auth() {
  const { t, login, register } = useApp();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [mode, setMode] = useState(params.get("mode") === "owner" ? "signup" : "signin");
  const [role, setRole] = useState(params.get("mode") === "owner" ? "owner" : "guest");
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [busy, setBusy] = useState(false);
  const redirectAfter = params.get("redirect") || "";

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const user = mode === "signin"
        ? await login(form.email, form.password)
        : await register({ ...form, role });
      toast.success(`Welcome ${user.name}!`);
      // Se abbiamo un redirect (es. da PoolDetail), lo onoriamo — vale per tutti i ruoli
      if (redirectAfter) {
        nav(decodeURIComponent(redirectAfter));
      } else {
        nav(user.role === "owner" ? "/owner" : user.role === "admin" ? "/admin" : "/search");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    localStorage.setItem("pending_role", role);
    if (redirectAfter) localStorage.setItem("pending_redirect", redirectAfter);
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="max-w-md mx-auto px-6 py-16">
      <div className="bg-white border border-border rounded-2xl p-8 shadow-sm">
        <h1 className="font-display text-3xl font-bold mb-2">{mode === "signin" ? t("auth.signin") : t("auth.signup")}</h1>
        <p className="text-sm text-muted-foreground mb-6">PoolPass</p>
        <form onSubmit={submit} className="space-y-4">
          {mode === "signup" && (
            <>
              <div>
                <Label>{t("auth.name")}</Label>
                <Input required data-testid="input-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div>
                <Label>{t("auth.role")}</Label>
                <RadioGroup value={role} onValueChange={setRole} className="grid grid-cols-2 gap-2 mt-2">
                  <label className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer ${role === "guest" ? "border-[#005C7A] bg-[#005C7A]/5" : "border-border"}`}>
                    <RadioGroupItem value="guest" data-testid="role-guest" />
                    <span className="text-sm font-medium">{t("auth.guest")}</span>
                  </label>
                  <label className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer ${role === "owner" ? "border-[#005C7A] bg-[#005C7A]/5" : "border-border"}`}>
                    <RadioGroupItem value="owner" data-testid="role-owner" />
                    <span className="text-sm font-medium">{t("auth.owner")}</span>
                  </label>
                </RadioGroup>
              </div>
            </>
          )}
          <div>
            <Label>{t("auth.email")}</Label>
            <Input required type="email" data-testid="input-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </div>
          <div>
            <Label>{t("auth.password")}</Label>
            <Input required type="password" data-testid="input-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
          </div>
          <Button type="submit" disabled={busy} className="w-full rounded-full bg-[#005C7A] hover:bg-[#004A63] text-white" data-testid="submit-auth">
            {busy ? "..." : mode === "signin" ? t("auth.signin") : t("auth.signup")}
          </Button>
          {mode === "signin" && (
            <div className="text-right -mt-2">
              <Link to="/forgot-password" className="text-xs text-[#005C7A] hover:underline" data-testid="forgot-password-link">Password dimenticata?</Link>
            </div>
          )}
        </form>
        <div className="flex items-center gap-3 my-5"><div className="flex-1 h-px bg-border" /><span className="text-xs text-muted-foreground">{t("auth.or")}</span><div className="flex-1 h-px bg-border" /></div>
        <button onClick={googleLogin} className="w-full py-3 rounded-full border-2 border-border hover:bg-muted transition font-medium flex items-center justify-center gap-2" data-testid="google-login">
          <svg width="18" height="18" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
          {t("auth.google")}
        </button>
        <div className="text-center mt-6 text-sm">
          {mode === "signin" ? t("auth.noAccount") : t("auth.hasAccount")}{" "}
          <button onClick={() => setMode(mode === "signin" ? "signup" : "signin")} className="text-[#005C7A] font-semibold hover:underline" data-testid="switch-mode">
            {mode === "signin" ? t("auth.signup") : t("auth.signin")}
          </button>
        </div>
      </div>
      <p className="text-xs text-center text-muted-foreground mt-4">Test: marco8519@gmail.com (admin) · hotel.owner@poolpass.com (owner) · guest.demo@poolpass.com (guest) — all pw: Passw0rd!</p>
    </div>
  );
}
